import { useCallback, useEffect, useState } from 'react';
import { useVersionedApi } from '../../../utils/versionedApi';
import { MY_PLANS_PATH, isNotOffered } from './planAcknowledgmentModel';

const PAGE_SIZE = 50;

/**
 * The signed-in person's plans waiting for their acknowledgment.
 * status: 'idle' (not asked: disabled), 'loading', 'ok', 'hidden' (refused,
 * not offered or not available yet) or 'error'.
 *
 * @param {{ enabled?: boolean, programId?: string, patientId?: string }} options
 */
export default function useMyPlansToRead({ enabled = true, programId, patientId } = {}) {
  const api = useVersionedApi('v1');
  const [state, setState] = useState({ status: enabled ? 'loading' : 'idle', plans: [], total: 0, summary: { pending: 0, overdue: 0 } });
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!enabled) { setState({ status: 'idle', plans: [], total: 0, summary: { pending: 0, overdue: 0 } }); return undefined; }
    let active = true;
    setState((current) => ({ ...current, status: current.status === 'ok' ? 'ok' : 'loading' }));
    const params = { pageSize: PAGE_SIZE, ...(programId ? { programId } : {}), ...(patientId ? { patientId } : {}) };
    api.get(MY_PLANS_PATH, { params })
      .then((response) => {
        if (!active) return;
        const body = response?.data || {};
        setState({
          status: 'ok',
          plans: Array.isArray(body.data) ? body.data : [],
          total: body.pagination?.total ?? (Array.isArray(body.data) ? body.data.length : 0),
          summary: body.summary || { pending: 0, overdue: 0 },
        });
      })
      .catch((error) => {
        if (!active) return;
        if (!isNotOffered(error)) console.error('Plans to read failed to load', { status: error?.response?.status || 'network' });
        setState({ status: isNotOffered(error) ? 'hidden' : 'error', plans: [], total: 0, summary: { pending: 0, overdue: 0 } });
      });
    return () => { active = false; };
  }, [api, enabled, programId, patientId, revision]);

  const reload = useCallback(() => setRevision((value) => value + 1), []);
  return { ...state, reload };
}
